/**
 * Sentry configuration for error tracking and monitoring
 */

import * as Sentry from "@sentry/react"
declare const __APP_VERSION__: string

/**
 * Initialize Sentry for error tracking
 * Only enabled in production environment
 */
export function initSentry() {
  // Only initialize in production
  if (import.meta.env.VITE_USE_SENTRY !== "true") {
    console.log("Sentry: Disabled in development mode")
    return
  }

  Sentry.init({
    dsn: "http://474cb8c2cdef4f2487d87854977cb7e7@bugsink.kudzucode.com/2",

    // Environment tracking
    environment: import.meta.env.PROD ? "production" : "development",

    // Release tracking (can be set via environment variable)
    release: __APP_VERSION__,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
    // In production you'll likely want to reduce this
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.kudzucode\.com/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out errors we don't care about
    beforeSend(event, hint) {
      // Don't send events for localhost in development
      if (window.location.hostname === "localhost") {
        return null
      }

      // Filter out network errors from ad blockers, etc.
      const error = hint.originalException
      if (error && typeof error === "object" && "message" in error) {
        const message = String(error.message)
        if (
          message.includes("NetworkError") ||
          message.includes("Failed to fetch") ||
          message.includes("ERR_BLOCKED_BY_CLIENT")
        ) {
          return null
        }
      }

      return event
    },
  })
}

/**
 * Manually capture an exception to Sentry
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
) {
  if (context) {
    Sentry.setContext("custom", context)
  }
  Sentry.captureException(error)
}

/**
 * Manually capture a message to Sentry
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.captureMessage(message, level)
}

/**
 * Set user context for Sentry
 */
export function setUser(
  user: { id: number; name: string; slug: string } | null
) {
  if (user) {
    Sentry.setUser({
      id: String(user.id),
      username: user.slug,
      name: user.name,
    })
  } else {
    Sentry.setUser(null)
  }
}
